/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";

import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";
import MedicationsHeader from "./Header/MedicationsHeader";
import MedicationFilter from "./Popup/MedicationFilter";
import AddLabelModal from "components/Common/Modal/AddLabelModal";
import { removeTagHandler } from "helper/TagHelper/TagHelper";
import SaveMedicationPopup from "components/Common/Popup/SaveMedicationPopup";

const MedicationsListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [medicationsList, setMedicationsList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedMedicationIds, setSelectedMedicationIds] = useState([]);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedMedicationId, setSelectedMedicationId] = useState(null);

  const [isDataReady, setIsDataReady] = useState(false);

  // json filter query
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [isFilterReset, setIsFilterReset] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // get all medications
  const getAllMedications = async () => {
    try {
      setIsLoading(true);
      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_MEDICINE + `?token=${token}`;

      if (jsonFilterQuery.length > 0) {
        // Sanitize the jsonFilterQuery
        const sanitizedQuery = jsonFilterQuery.map((filter) => {
          if (
            (filter.operator === "includes" ||
              filter.operator === "excludes") &&
            Array.isArray(filter.value)
          ) {
            return {
              ...filter,
              value: filter.value.map((item) => item.value).join(","), // Extract and join values as a comma-separated string
            };
          }
          return filter;
        });

        requestUrl = requestUrl + `&filters=${JSON.stringify(sanitizedQuery)}`;
      }

      console.log("requestUrl in get all medications", requestUrl);

      const response = await getData(requestUrl);

      console.log("get all medications response", response);
      setIsLoading(false);
      if (response.status) {
        setMedicationsList(response.data);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  // bulk update medications
  const updateBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeUpdate = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeUpdate,
        };

        let requestURL =
          url.API_BASE_URL + url.API_UPDATE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedMedicationIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one medication");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  // bulk delete medications
  const deleteBulkMedicationsHandler = async () => {
    if (selectedMedicationIds.length > 0) {
      try {
        const medicationsListToBeDelete = medicationsList.filter((item) =>
          selectedMedicationIds.includes(item._id)
        );

        let medicationsData = {
          medicinelist: medicationsListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL + url.API_DELETE_BULK_MEDICINE + `?token=${token}`;

        const response = await putData(requestURL, medicationsData);

        console.log(response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setMedicationsList((prevList) =>
              prevList.filter(
                (item) => !selectedMedicationIds.includes(item._id)
              )
            );
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one medication");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //remove tag from medication
  const removeTagFromMedication = async (tagIds, componentId) => {
    try {
      const componentUrl = url.API_ADD_REMOVE_TAG_MEDICINE;
      const data = await removeTagHandler(tagIds, componentId, componentUrl);

      if (data) {
        afterActionClose(data);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setTimeout(() => {
        setSelectedTagId(null);
        setSelectedMedicationId(null);
      }, 1000);
    }
  };

  // After any action
  const afterActionClose = (data = []) => {
    setMedicationsList((prevList) => mergeListById(prevList, data));
  };

  const resetFilterData = () => {
    setSelectedMedicationIds([]);
    setRowSelection({});
    setJsonFilterQuery([]);
    setIsFilterReset(true);
    setReloadData(true);
  };

  useEffect(() => {
    if (jsonFilterQuery.length > 0) {
      getAllMedications();
      setIsDataReady(true);
    }
  }, [jsonFilterQuery]);

  useEffect(() => {
    if (reloadData) {
      getAllMedications();
      setReloadData(false);
      setIsDataReady(true);
    }
  }, [reloadData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: "Actions",
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#saveMedicationOffCanvas"
            aria-controls="saveMedicationOffCanvas"
            onClick={() => {
              setSelectedMedicationId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Medicine",
        accessorKey: "productname",
        size: 200,
        Cell: ({ row }) => (
          <div
            className={
              row.original.productname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.productimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.productimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.productname}</span>
            </p>
          </div>
        ),
      },
      //tags column
      {
        accessorKey: "tagstring",
        header: t("Tags"),
        size: 200,
        Cell: ({ row }) => (
          <div className="label-wrapper py-1">
            <div className="d-flex gap-2">
              <div
                className={
                  row.original.taglist.length > 0
                    ? "label-container flex-grow-1"
                    : "d-none"
                }
              >
                <ul
                  className="d-flex flex-wrap gap-2 m-0 p-0"
                  style={{ listStyle: "none" }}
                >
                  {row.original.taglist.map((tag, index) => (
                    <li key={index} className="d-inline-flex">
                      <div className="badge d-flex align-items-center px-3 py-2 border-0 gradient-light text-black rounded-pill">
                        <span className="badge-text fs-xs fw-medium me-1">
                          {tag.title}
                        </span>
                        <Link
                          to="#"
                          className={`${
                            tag.moderator.toString() ===
                              userInfo._id.toString() ||
                            userInfo.role.slug === "ADMIN" ||
                            userInfo.role.slug === "SUPER_ADMIN"
                              ? "d-block"
                              : "d-none"
                          }`}
                          onClick={() => {
                            setSelectedMedicationId(row.original._id);
                            setSelectedTagId(tag._id);
                            removeTagFromMedication(
                              [tag._id],
                              row.original._id
                            );
                          }}
                        >
                          {selectedTagId &&
                          selectedTagId === tag._id &&
                          selectedMedicationId &&
                          selectedMedicationId === row.original._id ? (
                            <div
                              className="spinner-border spinner-border-sm text-primary ms-1"
                              role="status"
                              style={{ height: "12px", width: "12px" }}
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          ) : (
                            <span
                              className="d-block text-primary material-symbols-outlined icon-sm ms-1"
                              style={{ cursor: "pointer" }}
                            >
                              cancel
                            </span>
                          )}
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                className="btn btn-light btn-sm rounded-circle p-1 shadow-sm border-0 d-flex align-items-center justify-content-center mt-1"
                style={{ width: "24px", height: "24px" }}
                data-bs-toggle="modal"
                data-bs-target="#addLabelModal"
                onClick={() => setSelectedMedicationIds([row.original._id])}
              >
                <span
                  className="material-symbols-outlined text-primary"
                  style={{ fontSize: "16px" }}
                >
                  add
                </span>
              </button>
            </div>
          </div>
        ),
      },
      {
        header: "Start Date",
        accessorKey: "medicinestartdate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "End Date",
        accessorKey: "medicineenddate",
        Cell: ({ cell }) => cell.getValue().split("T")[0],
      },
      {
        header: "Recommended by",
        accessorKey: "referredpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.referredpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.referredpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.referredpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.referredpersonname}</span>
            </p>
          </div>
        ),
      },
      {
        header: "Recommended For",
        accessorKey: "assignedpersonname",
        size: 200,
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage === ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="User"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
          </div>
        ),
      },
      //prescribed by
      // {
      //   header: "Prescribed by",
      //   accessorKey: "prescribedpersonname",
      //   size: 200,
      //   Cell: ({ row }) => (
      //     <div
      //       className={
      //         row.original.prescribedpersonname === ""
      //           ? "d-none"
      //           : "owner border-bottom-0 d-flex align-items-center gap-1"
      //       }
      //     >
      //       <img
      //         className="rounded-circle"
      //         height={35}
      //         width={35}
      //         src={
      //           row.original.prescribedpersonimage === ""
      //             ? assetImages.defaultUser
      //             : url.SERVER_URL + row.original.prescribedpersonimage
      //         }
      //         alt="User"
      //       />
      //       <p>
      //         <span className="d-block">
      //           {row.original.prescribedpersonname}
      //         </span>
      //       </p>
      //     </div>
      //   ),
      // },
      {
        header: "Direction / Usage",
        accessorKey: "comments",
        size: 200,
        Cell: ({ row }) => (
          <ul className="list-style-none p-0 m-0 d-flex flex-column gap-1">
            {row.original?.usagelist?.map((usage, index) => (
              <li key={index} className="text-gray fw-semibold">
                <span className="material-symbols-outlined icon-md text-danger me-1">
                  notifications_active
                </span>
                Dose {index + 1} : {usage.dose}mg at{" "}
                <span className="text-primary">{usage.time}</span>
              </li>
            ))}
          </ul>
        ),
      },
    ],
    [selectedMedicationId, selectedTagId, selectedMedicationIds]
  );

  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ];

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );
    if (selectedIdsArray.length > 0) {
      setSelectedMedicationIds(selectedIdsArray);
    } else {
      setSelectedMedicationIds([]);
    }
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    (moduleAccess.includes("MOD_PATIENT_MANAGEMENT") &&
      moduleAccess.includes("MOD_PATIENT_MEDICATION"))
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Medications")}
          />
          <div className="container-fluid px-lg-5">
            <MedicationsHeader
              selectedItemIds={selectedMedicationIds}
              updateBulkHandler={updateBulkMedicationsHandler}
              reloadList={resetFilterData}
              jsonFilterQuery={jsonFilterQuery}
            />

            {isLoading || !isDataReady ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={medicationsList}
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* popup and modals */}
          <SaveMedicationPopup
            selectedMedicationId={selectedMedicationId}
            setSelectedMedicationId={setSelectedMedicationId}
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <MedicationFilter
            moduleSlug="medicationslist"
            columns={columns}
            setJsonFilterQuery={setJsonFilterQuery}
            reloadList={resetFilterData}
            isFilterReset={isFilterReset}
            setIsFilterReset={setIsFilterReset}
          />

          <AssignUserModal
            moduleName="medicine"
            afterModalClose={afterActionClose}
            selectedIds={selectedMedicationIds}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedMedicationIds([]);
            }}
          />

          <AddLabelModal
            moduleName="medicine"
            selectedIds={selectedMedicationIds}
            afterTagModalClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
            onModalClose={() => {
              setRowSelection({});
              setSelectedMedicationIds([]);
            }}
          />

          <DeleteWarningModal
            onDelete={deleteBulkMedicationsHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedMedicationIds([]);
            }}
            moduleName={t("Medications")}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default MedicationsListBody;
